import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { BarChart3, Globe2, Briefcase, Wallet, LogOut, User, Sparkles, Activity, Archive } from "lucide-react";
import { SearchBar } from "./SearchBar";
import { CurrencySelector } from "./CurrencySelector";
import { LanguageSelector } from "./LanguageSelector";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";

const navItems = [
    { to: "/", labelKey: "nav_dashboard", icon: BarChart3 },
    { to: "/opportunities", labelKey: "nav_opportunities", icon: Sparkles },
    { to: "/macro", labelKey: "nav_macro", icon: Globe2 },
    { to: "/portfolio", labelKey: "nav_portfolio", icon: Briefcase },
    { to: "/budget", labelKey: "nav_budget", icon: Wallet },
    { to: "/trading", labelKey: "nav_open_trades", icon: Activity },
    { to: "/closed-positions", labelKey: "nav_closed_positions", icon: Archive },
];

export const Header = () => {
    const loc = useLocation();
    const nav = useNavigate();
    const { t } = useI18n();
    const { user, logout } = useAuth();

    const handleLogout = async () => {
        await logout();
        toast.success("Disconnesso");
        nav("/login", { replace: true });
    };
    return (
        <header data-testid="app-header" className="border-b border-soft bg-elev px-3 sm:px-4 py-2 flex flex-wrap items-center justify-between gap-2 sm:gap-4">
            <div className="flex items-center gap-3 sm:gap-6 flex-shrink-0">
                <Link to="/" data-testid="logo-link" className="flex items-center gap-2">
                    <img
                        src="/capitaly-icon.png"
                        alt="Capitaly"
                        width={36}
                        height={36}
                        className="flex-shrink-0"
                        style={{ display: "block" }}
                    />
                    <div className="flex flex-col leading-none">
                        <span
                            className="font-display font-bold text-lg tracking-tight"
                            style={{
                                background: "linear-gradient(135deg, var(--brand-blue) 0%, var(--brand-green) 100%)",
                                WebkitBackgroundClip: "text",
                                WebkitTextFillColor: "transparent",
                                backgroundClip: "text",
                            }}
                        >
                            Capitaly
                        </span>
                        <span className="text-[10px] text-mute tracking-widest hidden sm:block">FINANCE v2.0</span>
                    </div>
                </Link>
                <nav className="flex items-center gap-0.5 sm:gap-1 order-3 sm:order-2 w-full sm:w-auto overflow-x-auto">
                    {navItems.map(({ to, labelKey, icon: Icon }) => {
                        const active = loc.pathname === to || (to !== "/" && loc.pathname.startsWith(to));
                        const label = t(labelKey);
                        return (
                            <Link
                                key={to}
                                to={to}
                                data-testid={`nav-${labelKey}`}
                                className={`flex items-center gap-1.5 px-2 sm:px-3 py-2 text-[10px] sm:text-xs font-semibold tracking-wider border-b-2 whitespace-nowrap ${
                                    active ? "" : "border-transparent text-dim hover:text-foreground"
                                }`}
                                style={active ? { borderColor: "var(--amber)", color: "var(--amber)" } : undefined}
                            >
                                <Icon size={14} />
                                <span>{label}</span>
                            </Link>
                        );
                    })}
                </nav>
            </div>
            <div className="flex-1 min-w-[200px] max-w-md order-2 sm:order-3">
                <SearchBar />
            </div>
            <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0 order-4">
                <LanguageSelector />
                <CurrencySelector />
                {user && user !== false && user !== null && (
                    <div className="flex items-center gap-1">
                        <Link
                            to="/profile"
                            data-testid="nav-profile"
                            className="flex items-center gap-1.5 px-2 py-1.5 border border-soft hover:border-amber text-xs"
                            title={user.email}
                        >
                            {user.picture ? (
                                <img src={user.picture} alt="" width={18} height={18} className="rounded-full" />
                            ) : (
                                <div className="w-[18px] h-[18px] rounded-full flex items-center justify-center text-[9px] font-bold"
                                    style={{ background: "linear-gradient(135deg, var(--brand-blue), var(--brand-green))", color: "#000" }}>
                                    {(user.name || user.email || "U")[0].toUpperCase()}
                                </div>
                            )}
                            <span className="hidden md:inline" style={{ color: "var(--amber)" }}>{user.name?.split(" ")[0] || "Profilo"}</span>
                        </Link>
                        <button
                            data-testid="header-logout"
                            onClick={handleLogout}
                            title="Esci"
                            className="p-1.5 text-mute hover:text-red-down border border-soft"
                            style={{ color: "var(--red)" }}
                        >
                            <LogOut size={12} />
                        </button>
                    </div>
                )}
                <div className="hidden lg:flex items-center gap-2 text-xs">
                    <span className="w-2 h-2 rounded-full live-dot" style={{ background: "var(--green)" }}></span>
                    <span className="text-mute">LIVE</span>
                </div>
            </div>
        </header>
    );
};
